import React from "react";

import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight, FaExclamationCircle } from "react-icons/fa";
import AuthFooter from "../../components/layout/footer/AuthFooter";
import AuthMechanism from "../../components/auth/AuthMechanism";
import { clickHere, commonErrMsg, login } from "../../locales/en";
import AuthHeader from "../../components/auth/AuthHeader";
import { useSelector } from "react-redux";
import "./../../assets/scss/login.scss";
import EmailField from "./EmailField";
import PasswordField from "./PasswordField";
import OtpCodeInput from "./OtpCodeInput";

import useLogin from "../../hooks/login/useLogin";
import { RESET_PASSWORD, SIGN_UP } from "../../route/paths";
import {
  CONTACT_SUPPORT,
  CONTACT_SUPPORT_LINK,
  LINK_REL
} from "../../utils/constants/appConstants";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const Login = () => {
  const environment = process.env.REACT_APP_ENV;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const {
    values,
    errors,
    loading,
    success,
    twoFAVerification,
    loginSubmitButton,
    qrCodeUrl,
    qrCodeVisible,
    errorMessage,
    handleInputChange,
    handleClickShowPassword,
    handleSentTwoFaQrcode,
    handleSubmit,
    handleSubmitTwoFACode,
    handleBackToLogin
  } = useLogin(environment, executeRecaptcha);
  const { loginErrorMessage } = useSelector((state) => state.login);

  // The 2FA code step takes over the same panel area the credentials form
  // occupied.
  const otpStep = twoFAVerification;
  const otpValue = values.twoFactorCode;
  const otpField = "twoFactorCode";
  const otpError = errors.invalidMessage || errors.twoFactorCode;
  const otpHeading = "Verify it's you";
  const otpSubheading = "Enter the 6-digit code from your authenticator app.";
  const otpLabel = "Authentication code";
  const otpErrorId = "otp-code-error";

  // Sending a code and submitting one both mutate the same server-side code, so
  // each control disables while any of them is in flight: a resend soft-deletes
  // the code an in-flight submit is verifying, which would land an "expired"
  // rejection on boxes the resend has just cleared.
  const otpBusy =
    loading.loginBtn || loading.sendTwoFaBtn || loading.reSendTwoFaBtn;
  const otpSubmitting = twoFAVerification
    ? loading.sendTwoFaBtn
    : loading.loginBtn;

  // Route the form's submit (incl. pressing Enter in the OTP boxes): in the
  // 2FA step Enter must verify the code, not re-run the credentials login —
  // which would reset the 2FA state and discard the code just typed.
  const handleFormSubmit = (event) => {
    if (twoFAVerification) {
      handleSubmitTwoFACode(event);
    } else {
      handleSubmit(event);
    }
  };

  return (
    <div className="auth-viewport">
      <AuthHeader />
      <main className="auth-page">
        <div className="auth-layout">
          <AuthMechanism who="mjwebs" />
          <div className="auth-right-panel">
            <div className="auth-form-wrap">
              {otpStep && (
                <button
                  type="button"
                  className="auth-back-link"
                  onClick={handleBackToLogin}
                >
                  <FaArrowLeft aria-hidden /> Back to login
                </button>
              )}
              <h1 className="auth-heading">
                {otpStep ? otpHeading : "Welcome back"}
              </h1>
              <p className="auth-subheading">
                {otpStep ? otpSubheading : "Log in to your FundTap account."}
              </p>

              {!otpStep && loginErrorMessage?.message && (
                <div className="auth-form-error">
                  {loginErrorMessage?.type === CONTACT_SUPPORT ? (
                    <>
                      {loginErrorMessage.message}
                      <div className="mt-1">
                        {`${commonErrMsg?.item16} `}
                        <Link
                          to={CONTACT_SUPPORT_LINK}
                          target="_blank"
                          rel={LINK_REL}
                        >
                          {commonErrMsg?.item17}
                        </Link>
                      </div>
                    </>
                  ) : (
                    loginErrorMessage?.message || loginErrorMessage
                  )}
                </div>
              )}

              <form
                id="login_submit_form"
                className="auth-page-form"
                onSubmit={handleFormSubmit}
              >
                {!otpStep ? (
                  <>
                    <EmailField
                      value={values.email}
                      error={errors.email}
                      onChange={(v) => handleInputChange("email", v)}
                      label={login?.loginBox.emailText}
                    />

                    <PasswordField
                      value={values.password}
                      show={values.showPassword}
                      error={errors.password}
                      onChange={(v) => handleInputChange("password", v)}
                      onToggle={handleClickShowPassword}
                      label={login?.loginBox.passwordText}
                    />
                    <div className="auth-forgot-row">
                      <Link to={RESET_PASSWORD} className="auth-forgot-link">
                        {login?.loginBox.forgotBTN}
                      </Link>
                    </div>

                    {errors.reCaptcha && (
                      <div className="input-row">
                        <div className="input-group">
                          <p className="error-message" role="alert">
                            <FaExclamationCircle
                              className="error-icon"
                              aria-hidden
                            />
                            <small>{errors.reCaptcha}</small>
                          </p>
                        </div>
                      </div>
                    )}

                    {loginSubmitButton && (
                      <div className="input-row">
                        <div className="input-group">
                          <button
                            type="submit"
                            className="auth-btn-secondary"
                            disabled={loading.loginBtn}
                          >
                            {loading.loginBtn ? (
                              <>
                                <span
                                  className="auth-spinner"
                                  aria-hidden="true"
                                />
                                Signing in…
                              </>
                            ) : (
                              <>
                                Log in <FaArrowRight />
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {qrCodeVisible && qrCodeUrl && (
                      <div className="auth-otp-qr">
                        <p className="auth-otp-qr-text">
                          {login?.loginBox.scanQR}
                        </p>
                        <div className="auth-otp-qr-img">
                          <img src={qrCodeUrl} alt="Authenticator QR code" />
                        </div>
                      </div>
                    )}

                    <div className="input-row auth-otp-row">
                      <label className="auth-otp-label" htmlFor="otp-code-0">
                        {otpLabel}
                      </label>
                      <OtpCodeInput
                        length={6}
                        value={otpValue || ""}
                        error={Boolean(otpError)}
                        ariaLabel={otpLabel}
                        describedBy={otpError ? otpErrorId : undefined}
                        onChange={(v) => handleInputChange(otpField, v)}
                      />
                      {otpError && (
                        <p
                          className="error-message"
                          id={otpErrorId}
                          role="alert"
                        >
                          <FaExclamationCircle
                            className="error-icon"
                            aria-hidden
                          />
                          <small>{otpError}</small>
                        </p>
                      )}
                      {twoFAVerification && success.twoFactorResent && (
                        <p className="error-message text-success">
                          <small>{success.twoFactorResent}</small>
                        </p>
                      )}
                    </div>

                    <div className="input-row">
                      <div className="input-group">
                        {/* Always type=submit so pressing Enter in the code
                            boxes verifies; handleFormSubmit routes the
                            credentials step and the 2FA step to their own
                            handlers. */}
                        <button
                          type="submit"
                          className="auth-btn-secondary"
                          disabled={otpBusy}
                        >
                          {otpSubmitting ? (
                            <>
                              <span
                                className="auth-spinner"
                                aria-hidden="true"
                              />
                              Verifying…
                            </>
                          ) : (
                            login?.loginBox.verifylogin
                          )}
                        </button>
                      </div>
                    </div>

                    <p className="auth-otp-resend">
                      {`${login?.loginBox.havingTrouble} `}
                      <button
                        type="button"
                        onClick={handleSentTwoFaQrcode}
                        disabled={otpBusy}
                      >
                        {clickHere}
                      </button>
                    </p>

                    {errorMessage && (
                      <div className="auth-form-error">{errorMessage}</div>
                    )}
                  </>
                )}

                {errors.general && (
                  <div className="auth-form-error">{errors.general}</div>
                )}
              </form>

              {!otpStep && (
                <p className="auth-toggle-text">
                  New to FundTap? <Link to={SIGN_UP}>Sign up</Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      <AuthFooter />
    </div>
  );
};

export default Login;
